import type { Slide } from '../../../types';

interface UseLockManagementProps {
  deckId: string;
  currentSlideId: number | null;
  currentSlideIndex: number;
  slides: Slide[];
  setSlides: (slides: Slide[]) => void;
  apiUrl: (path: string) => string;
}

/**
 * Hook for slide lock management
 *
 * Handles:
 * - Toggling slide lock state
 * - Setting unlock triggers
 * - Managing lock position (above/below)
 * - Lock z-index coordination
 */
export function useLockManagement({
  deckId,
  currentSlideId,
  currentSlideIndex,
  slides,
  setSlides,
  apiUrl,
}: UseLockManagementProps) {

  const toggleSlideLock = async () => {
    if (!currentSlideId) return;

    const currentSlide = slides[currentSlideIndex];
    const newLockedState = !currentSlide.locked;

    try {
      const response = await fetch(apiUrl(`/${deckId}/slides/${currentSlideId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            locked: newLockedState,
            lock_position: newLockedState ? 'above' : null,
            z_index: newLockedState ? 10 : 1
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to update slide');

      const updatedSlides = [...slides];
      updatedSlides[currentSlideIndex] = {
        ...updatedSlides[currentSlideIndex],
        locked: newLockedState,
        lock_position: newLockedState ? 'above' : undefined,
        z_index: newLockedState ? 10 : 1
      };
      setSlides(updatedSlides);
    } catch (error) {
      console.error('Failed to toggle slide lock:', error);
    }
  };

  const lockSlideWithUnlockSetting = async (unlockOnSlide: number | null) => {
    if (!currentSlideId) return;

    try {
      const response = await fetch(apiUrl(`/${deckId}/slides/${currentSlideId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            locked: true,
            lock_position: 'above',
            z_index: 10,
            unlock_on_slide: unlockOnSlide
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to lock slide');

      const updatedSlides = [...slides];
      updatedSlides[currentSlideIndex] = {
        ...updatedSlides[currentSlideIndex],
        locked: true,
        lock_position: 'above',
        z_index: 10,
        unlock_on_slide: unlockOnSlide !== null ? unlockOnSlide : undefined
      };
      setSlides(updatedSlides);
    } catch (error) {
      console.error('Failed to lock slide:', error);
    }
  };

  const updateLockPosition = async (position: 'above' | 'below') => {
    if (!currentSlideId) return;

    const zIndex = position === 'above' ? 10 : 0;

    try {
      const response = await fetch(apiUrl(`/${deckId}/slides/${currentSlideId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            lock_position: position,
            z_index: zIndex
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to update lock position');

      const updatedSlides = [...slides];
      updatedSlides[currentSlideIndex] = {
        ...updatedSlides[currentSlideIndex],
        lock_position: position,
        z_index: zIndex
      };
      setSlides(updatedSlides);
    } catch (error) {
      console.error('Failed to update lock position:', error);
    }
  };

  const updateUnlockSlide = async (slideIndex: number | null) => {
    if (!currentSlideId) return;

    try {
      const response = await fetch(apiUrl(`/${deckId}/slides/${currentSlideId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            unlock_on_slide: slideIndex
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to update unlock condition');

      const updatedSlides = [...slides];
      updatedSlides[currentSlideIndex] = {
        ...updatedSlides[currentSlideIndex],
        unlock_on_slide: slideIndex !== null ? slideIndex : undefined
      };
      setSlides(updatedSlides);
    } catch (error) {
      console.error('Failed to update unlock condition:', error);
    }
  };

  return {
    toggleSlideLock,
    lockSlideWithUnlockSetting,
    updateLockPosition,
    updateUnlockSlide,
  };
}
